/**
 * CustVideo.tsx — Feature-rich video player with three distinct playback modes,
 * thumbnail overlay, and mobile-aware responsive sizing.
 *
 * JSON trigger: `uiCompType.method: "renderCustVideo"`
 * Data field:   `sectionData.videoUrl` (supports hash-encoded CSS via `extractStylesFromUrl`)
 *
 * **Playback modes (determined by `uiCompType.options`):**
 *
 * | Mode              | Trigger                     | Behaviour |
 * |---                |---                          |---        |
 * | **autoPlay**      | `options.autoPlay: true`    | Video plays immediately, no overlay shown |
 * | **playOnMouseEnter** | `options.playOnMouseEnter: true` | 300 ms hover delay, pauses on mouse leave |
 * | **click-to-play** | default (no above flags)    | Custom thumbnail overlay with Framer Motion animated MUI play button; hides on first play |
 *
 * **Thumbnail overlay:** Set `options.thumbnail` to an image URL. Displayed
 * using Next.js `<Image fill>` inside the container. Fades out via Framer Motion
 * `<AnimatePresence>` when the user clicks play.
 *
 * **Key options:**
 *  - `thumbnail`       — URL for the poster/overlay image
 *  - `playOnMouseEnter`— Enable hover-to-play mode
 *  - `autoPlay`        — Start playing on mount
 *  - `loop`            — Loop video playback
 *  - `muted`           — Mute by default (required for autoPlay in most browsers)
 *  - `controls`        — Show native browser video controls
 *  - `poster`          — Native `<video poster>` attribute
 *  - `preloadVideo`    — `"auto"` | `"metadata"` | `"none"`
 *  - `borderRadius`    — Rounded corners on the container
 *  - `playsInline`     — Required for iOS inline playback
 *
 * **Style slots:** `containerStyles`/`mContainerStyles`, `styles`/`mStyles`,
 * plus hash-encoded styles in `videoUrl`.
 *
 * **Dependencies:** Framer Motion (overlay animation), MUI `IconButton` +
 * `PlayArrowRoundedIcon`, Next.js `<Image>`, `useDeviceType` hook.
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import cssStyles from "./cms_renderer.module.scss";
import { SectionData, ComponentRenderProps, UiCompTypeOptions } from "./index";
import { extractStylesFromUrl, parseDimension } from "./shared";
import { trackItem } from "@/utils";
import { useDeviceType } from "@/hooks/useDeviceType";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { IconButton } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";

interface CustVideoProps {
    sectionData: SectionData;
    props: ComponentRenderProps;
    index?: number;
}

const CustVideo = ({ sectionData, props, index = 1 }: CustVideoProps): JSX.Element | null => {
    //console.log("CustVideo - sectionData:", sectionData);
    // All hooks must come first, before any early returns or conditional logic
    const deviceType = useDeviceType();
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const mouseEnterTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Initialize with null check for early calculations
    const { stylesFromUrl, urlStr } = useMemo(() =>
        sectionData.videoUrl ? extractStylesFromUrl(sectionData.videoUrl as string) : { stylesFromUrl: {}, urlStr: "" }
        , [sectionData.videoUrl]);

    const videoStylesFromUiComp = useMemo(() => sectionData.uiCompType?.styles || {}, [sectionData.uiCompType?.styles]);
    const videoOptions = useMemo(() => sectionData.uiCompType?.options || {} as UiCompTypeOptions, [sectionData.uiCompType?.options]);

    // Memoize style objects to prevent infinite re-renders
    const mVideoStyles = useMemo(() => ({ ...sectionData.uiCompType?.mStyles }), [sectionData.uiCompType?.mStyles]);
    const mVideoContainerStyles = useMemo(() => ({
        ...sectionData.uiCompType?.mContainerStyles,
        ...sectionData.uiCompType?.mParentStyles
    }), [sectionData.uiCompType?.mContainerStyles, sectionData.uiCompType?.mParentStyles]);

    const baseVideoStyles = useMemo(() => ({ ...videoStylesFromUiComp, ...stylesFromUrl }), [videoStylesFromUiComp, stylesFromUrl]);
    const initialContainerStyles = useMemo(() => ({
        ...sectionData.uiCompType?.containerStyles,
        ...sectionData.uiCompType?.parentStyles
    }), [sectionData.uiCompType?.containerStyles, sectionData.uiCompType?.parentStyles]);

    const applyDimensionsToContainer = useCallback((
        containerStyles: Record<string, any>,
        videoStyles: Record<string, any>
    ) => {
        const result = { ...containerStyles };
        if (!result.height && !result.width) {
            if (videoStyles.width) {
                result.width = (typeof videoStyles.width === "number") ? `${videoStyles.width}px` : videoStyles.width;
            }
            if (videoStyles.height) {
                result.height = (typeof videoStyles.height === "number") ? `${videoStyles.height}px` : videoStyles.height;
                if (!result.width) {
                    result.width = '100%';
                }
            }
        }
        return result;
    }, []);

    // Apply dimensions without mutating original objects
    const processedInitialContainerStyles = useMemo(() =>
        applyDimensionsToContainer(initialContainerStyles, baseVideoStyles)
        , [initialContainerStyles, baseVideoStyles, applyDimensionsToContainer]);

    const processedMVideoContainerStyles = useMemo(() =>
        applyDimensionsToContainer(mVideoContainerStyles, mVideoStyles)
        , [mVideoContainerStyles, mVideoStyles, applyDimensionsToContainer]);

    // State hooks
    const [styles, setStyles] = useState(baseVideoStyles);
    const [containerStyles, setContainerStyles] = useState(processedInitialContainerStyles);
    const [isPlaying, setIsPlaying] = useState(videoOptions.autoPlay || false);
    const [showOverlay, setShowOverlay] = useState(!videoOptions.autoPlay); // Show overlay for all non-autoplay videos initially
    const [url, setUrl] = useState<string>(
        videoOptions.autoPlay ? (urlStr as string) : ""
    );
    const [videoPaused, setVideoPaused] = useState(!videoOptions.autoPlay);

    // All useEffect hooks must be before early return
    useEffect(() => {
        if (deviceType === "Mobile") {
            //console.log("CustVideo - Mobile styles applied", mVideoStyles, mVideoContainerStyles);
            if (Object.keys(mVideoStyles).length > 0) {
                setStyles({ ...baseVideoStyles, ...mVideoStyles });
            } else {
                setStyles({ ...baseVideoStyles, width: "100%" });
            }
            if (Object.keys(processedMVideoContainerStyles).length > 0) {
                setContainerStyles({ ...processedInitialContainerStyles, ...processedMVideoContainerStyles });
            } else {
                setContainerStyles({ ...processedInitialContainerStyles, width: "100%" });
            }
        } else {
            setStyles(baseVideoStyles);
            setContainerStyles(processedInitialContainerStyles);
        }
    }, [deviceType, baseVideoStyles, processedInitialContainerStyles, mVideoStyles, processedMVideoContainerStyles, mVideoContainerStyles]);

    // Effect for playOnMouseEnter functionality and general video setup
    useEffect(() => {
        const video = videoRef.current;

        // Handle clicks outside video for focus-based pause (pause only behavior)
        const handleClickOutside = (event: MouseEvent) => {
            if (!videoOptions.autoPlay &&
                containerRef.current &&
                !containerRef.current.contains(event.target as Node) &&
                videoRef.current &&
                !videoRef.current.paused) {
                videoRef.current.pause();
                setVideoPaused(true);
            }
        };

        // Set initial state for playOnMouseEnter
        if (videoOptions.playOnMouseEnter && !videoOptions.autoPlay) {
            setUrl(urlStr as string);
            // Don't auto-play, just make ready for mouse enter
            setShowOverlay(true); // Show overlay initially
            if (video) {
                video.pause(); // Start paused
                setVideoPaused(true);
                setIsPlaying(false);
            }
        }

        // Set initial state for regular click-to-play videos (non-autoplay, non-mouse-enter)
        if (!videoOptions.autoPlay && !videoOptions.playOnMouseEnter) {
            setUrl(urlStr as string);
            setShowOverlay(true); // Show overlay for click-to-play
            if (video) {
                video.pause(); // Start paused, waiting for click
                setVideoPaused(true);
                setIsPlaying(false);
            }
        }

        // Click outside handler for all non-autoplay videos
        if (!videoOptions.autoPlay) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        // Cleanup timeout on unmount
        return () => {
            if (mouseEnterTimeoutRef.current) {
                clearTimeout(mouseEnterTimeoutRef.current);
            }
            if (!videoOptions.autoPlay) {
                document.removeEventListener('mousedown', handleClickOutside);
            }
        };
    }, [videoOptions.playOnMouseEnter, videoOptions.autoPlay, urlStr]);

    // Effect to show overlay when video is paused
    useEffect(() => {
        if (videoOptions.autoPlay) {
            // Autoplay videos never show overlay
            setShowOverlay(false);
        } else if (videoPaused) {
            // All non-autoplay videos show overlay when paused
            setShowOverlay(true);
        } else {
            // Playing videos hide overlay
            setShowOverlay(false);
        }
    }, [videoPaused, videoOptions.autoPlay]);

    // Effect for window focus functionality (auto-pause on blur only, no auto-play on focus)
    useEffect(() => {
        const video = videoRef.current;
        if (videoOptions.autoPlay) return; // Skip for autoplay videos

        // Only handle window blur to pause videos, no auto-play on focus
        const handleWindowBlur = () => {
            if (video && !video.paused) {
                video.pause();
            }
        };

        window.addEventListener('blur', handleWindowBlur);

        return () => {
            window.removeEventListener('blur', handleWindowBlur);
        };
    }, [videoOptions.autoPlay]);

    // Effect for regular mode videos - handle video state changes
    useEffect(() => {
        const video = videoRef.current;
        if (!video || videoOptions.autoPlay) {
            return; // Only skip for autoplay videos
        }

        // For all non-autoplay videos, sync overlay with video state
        const handlePlay = () => {
            setIsPlaying(true);
            setVideoPaused(false);
            setShowOverlay(false);
        };

        const handlePause = () => {
            setIsPlaying(false);
            setVideoPaused(true);
            setShowOverlay(true);
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setVideoPaused(true);
            setShowOverlay(true);
        };

        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('ended', handleEnded);

        return () => {
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('ended', handleEnded);
        };
    }, [videoOptions.autoPlay]);

    // Early return after all hooks
    if (!sectionData.videoUrl) return null;

    const handleContainerClick = () => {
        const video = videoRef.current;
        if (!video) return;

        // For autoPlay videos, don't interfere with playback
        if (videoOptions.autoPlay) return;

        // For playOnMouseEnter, don't interfere with their specific behavior
        if (videoOptions.playOnMouseEnter) return;

        // Click-to-play mode: toggle play/pause
        if (video.paused || showOverlay) {
            // Video is paused or overlay is showing - play the video
            setUrl(urlStr as string);
            setTimeout(() => {
                if (video) {
                    video.play().catch(console.error);
                }
            }, 100);
            setIsPlaying(true);
            setShowOverlay(false);
            setVideoPaused(false);
        } else {
            // Video is playing - pause it and show overlay
            video.pause();
            setIsPlaying(false);
            setShowOverlay(true);
            setVideoPaused(true);
        }
    };

    const togglePlay = () => {
        const video = videoRef.current;
        if (!video) return;

        if (video.paused) {
            setUrl(urlStr as string);
            setTimeout(() => {
                if (video) {
                    video.play().catch(console.error);
                }
            }, 100);
            setIsPlaying(true);
            setShowOverlay(false);
            setVideoPaused(false);
        } else {
            video.pause();
            setIsPlaying(false);
            setShowOverlay(true);
            setVideoPaused(true);
        }
    };

    // Handle focus functionality (only pause on blur, no auto-play on focus)
    const handleVideoFocus = () => {
        // Do not auto-play on focus - videos should only play via click or mouse enter
        // This just gives the element focus for keyboard accessibility
    };

    const handleVideoBlur = () => {
        if (!videoOptions.autoPlay && videoRef.current) {
            videoRef.current.pause();
            setVideoPaused(true);
            // Video pauses on blur but doesn't show overlay unless it's not playing
        }
    };

    // Handle playOnMouseEnter functionality
    const handleMouseEnter = () => {
        if (videoOptions.playOnMouseEnter && videoRef.current) {
            // Clear any existing timeout
            if (mouseEnterTimeoutRef.current) {
                clearTimeout(mouseEnterTimeoutRef.current);
            }

            // Add delay to avoid unnecessary plays on simple mouse moves
            mouseEnterTimeoutRef.current = setTimeout(() => {
                if (videoRef.current) {
                    setUrl(urlStr as string);
                    setTimeout(() => {
                        if (videoRef.current) {
                            videoRef.current.play();
                            setIsPlaying(true);
                            setVideoPaused(false);
                        }
                    }, 100);
                }
            }, 300); // 300ms delay
        }
    };

    const handleMouseLeave = () => {
        if (videoOptions.playOnMouseEnter && videoRef.current) {
            // Clear timeout if mouse leaves before delay
            if (mouseEnterTimeoutRef.current) {
                clearTimeout(mouseEnterTimeoutRef.current);
                mouseEnterTimeoutRef.current = null;
            }

            if (!videoRef.current.paused) {
                videoRef.current.pause();
                setVideoPaused(true);
                // Immediately show play button on mouse exit
                setShowOverlay(true);
                setIsPlaying(false);
            }
        }
    };

    // Create the video container element to avoid duplication
    const videoContainer = (
        <div
            key={"VideoContainer-" + String(sectionData.trackId || index)}
            className={cssStyles.videoContainer}
            ref={containerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleContainerClick}
            onFocus={handleVideoFocus}
            onBlur={handleVideoBlur}
            tabIndex={!videoOptions.autoPlay ? 0 : -1}
            style={{
                position: 'relative',
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                cursor: !videoOptions.autoPlay ? "pointer" : "default",
                ...containerStyles
            }}
        >
            {/* Thumbnail or Background - show when not playing */}
            {!isPlaying && (
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: videoOptions?.thumbnail ? "transparent" : "#ffffff",
                        borderRadius: videoOptions?.borderRadius ? videoOptions.borderRadius : "inherit",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {videoOptions?.thumbnail && (
                        <Image
                            src={videoOptions.thumbnail as string}
                            alt="Video thumbnail"
                            fill
                            style={{
                                objectFit: "cover",
                                borderRadius: videoOptions?.borderRadius ? videoOptions.borderRadius : "inherit",
                            }}
                        />
                    )}
                </div>
            )}

            {/* Video Element */}
            <video
                ref={videoRef}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: videoOptions.autoPlay || isPlaying || videoOptions.playOnMouseEnter ? "block" : "block", // Always show video element for proper sizing
                    borderRadius: "inherit",
                    opacity: videoOptions.autoPlay || isPlaying || videoOptions.playOnMouseEnter ? 1 : 0, // Use opacity instead of display for sizing
                    ...styles,
                }}
                src={urlStr as string} // Always use the full URL for regular mode
                className={cssStyles.video}
                autoPlay={videoOptions.autoPlay || false}
                loop={videoOptions.loop ?? false}
                muted={videoOptions.muted ?? false}
                poster={videoOptions.poster || ''}
                preload={videoOptions.preloadVideo || 'auto'}
                playsInline={videoOptions.autoPlay ? true : (videoOptions.playsInline || false)}
                controls={(() => {
                    // If custom play button overlay is showing, hide controls
                    if (!videoOptions.autoPlay && showOverlay) {
                        return false; // Hide controls when custom overlay is showing
                    }

                    // Show controls based on videoOptions.controls setting
                    return videoOptions.controls ?? true;
                })()}
            />

            {/* Overlay Play Button - show when not autoPlay and video is paused */}
            {!videoOptions.autoPlay && showOverlay && (
                <AnimatePresence>
                    <motion.div
                        key="play"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.25 }}
                        style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: videoOptions?.thumbnail ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.1)",
                            borderRadius: "inherit",
                        }}
                    >
                        <IconButton
                            onClick={videoOptions.playOnMouseEnter ?
                                (e: React.MouseEvent) => {
                                    e.stopPropagation(); // Prevent bubbling to container click
                                    if (videoRef.current) {
                                        videoRef.current.play();
                                        setVideoPaused(false);
                                    }
                                } : (e: React.MouseEvent) => {
                                    e.stopPropagation(); // Prevent bubbling to container click
                                    togglePlay();
                                }
                            }
                            sx={{
                                backgroundColor: "#fff",
                                width: 56,
                                height: 56,
                                boxShadow: 3,
                                "&:hover": {
                                    transform: "scale(1.08)",
                                    backgroundColor: "#fff",
                                },
                                transition: "transform 0.2s ease",
                            }}
                        >
                            <PlayArrowRoundedIcon sx={{ color: "teal", fontSize: 34 }} />
                        </IconButton>
                    </motion.div>
                </AnimatePresence>
            )}
        </div>
    );

    return (
        <>
            {sectionData.link ? (
                <a
                    href={sectionData.link}
                    target={sectionData.newTab ? "_blank" : "_self"}
                    onClick={(e) => {
                        trackItem(sectionData.trackInfo || { event: 'custVideoLinkClick', section: sectionData.trackId });
                    }}
                    style={{ display: 'block' }}
                >
                    {videoContainer}
                </a>
            ) : (
                videoContainer
            )}
        </>
    );
}
export default CustVideo;